import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import type { ParameterTimeSeriesConfiguration, ParametersLocation } from "@/lib/api";
import type { ParameterTimeSeriesChartResult } from "@/lib/api";
import {
  resolveChartConfigUnit,
  resolveChartSeriesLabel,
  toParameterTimeSeriesChartResultFromObserved,
} from "@/lib/api/parameter-time-series-chart-result.utils";
import { TimeSeriesConfig } from "@/components/graph/graph.types";
import { useMapTimeseriesFetchRequests } from "@/components/map/map-control-timeseries-content";

import {
  buildChartParameterSeriesRequestsForLocation,
  observedLocationParameterDataQuery,
} from "./hooks/queries";
import { mergeStationTimeseriesChartData } from "./merge-station-timeseries-chart-data";

type UseStationTimeseriesChartDataParams = {
  location: ParametersLocation;
  timeSeriesConfigs: TimeSeriesConfig[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
};

type StationTimeseriesChartData = {
  /** One entry per chart series (config order), with empty placeholders merged in. */
  chartData: ParameterTimeSeriesChartResult[];
  /** Raw fetched results (only parameters the station actually returned data for). */
  chartResults: ParameterTimeSeriesChartResult[];
  /** True while any series query is still loading. */
  pending: boolean;
};

/**
 * Fetches and shapes the per-station time-series data a chart needs: it resolves
 * the parameters referenced by {@link TimeSeriesConfig}s, fires one observed
 * series query per supported (location, parameter, unit), and merges the results
 * into config order (empty placeholders for configured-but-missing series).
 *
 * Extracted from the station click panel so both the dashboard panel and the CMS
 * chart preview drive charts from the same pipeline.
 */
export function useStationTimeseriesChartData({
  location,
  timeSeriesConfigs,
  timeSeriesConfigurations,
}: UseStationTimeseriesChartDataParams): StationTimeseriesChartData {
  const chartParameterIds = useMapTimeseriesFetchRequests(timeSeriesConfigs).map(
    (request) => request.parameterId,
  );

  const seriesRequests = useMemo(
    () =>
      buildChartParameterSeriesRequestsForLocation(
        location,
        timeSeriesConfigurations,
        chartParameterIds,
      ),
    [location, timeSeriesConfigurations, chartParameterIds],
  );

  const combinedQueries = useQueries({
    queries: seriesRequests.map(({ locationId, parameterId, unitCode }) => ({
      ...observedLocationParameterDataQuery(locationId, parameterId, unitCode),
      placeholderData: keepPreviousData,
    })),
    combine: (queryResults) => ({
      entries: queryResults
        .map((result, index) => ({
          request: seriesRequests[index],
          data: result.data,
        }))
        .filter(
          (
            entry,
          ): entry is {
            request: (typeof seriesRequests)[number];
            data: NonNullable<(typeof entry)["data"]>;
          } => entry.data !== undefined,
        ),
      pending: queryResults.some((result) => result.isPending),
    }),
  });

  const chartResults = useMemo(
    () =>
      combinedQueries.entries.map(({ request, data }) =>
        toParameterTimeSeriesChartResultFromObserved(data, {
          parameterLabel: resolveChartSeriesLabel(request.parameterId, timeSeriesConfigs),
          unit: resolveChartConfigUnit(request.parameterId, timeSeriesConfigurations),
        }),
      ),
    [combinedQueries.entries, timeSeriesConfigs, timeSeriesConfigurations],
  );

  const chartData = useMemo(
    () => mergeStationTimeseriesChartData(timeSeriesConfigs, chartResults),
    [timeSeriesConfigs, chartResults],
  );

  return { chartData, chartResults, pending: combinedQueries.pending };
}
